import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { CheckCircle2, ExternalLink, RefreshCw, ShieldCheck } from 'lucide-react';

import { useLanguage } from '../../i18n/language.js';
import {
  fetchPoolReviewQueue,
  submitPoolReview,
  type PoolIdentityReviewState,
  type PoolReviewQueueEntry,
  type PoolRightsReviewState,
  type PoolSafetyReviewState,
} from '../api.js';
import { InlineAlert, Spinner } from '../ui.js';

const COPY = {
  en: {
    eyebrow: 'Owner · staging only',
    title: 'Video pool review',
    intro:
      'Review source-reference rights, safety, and duplicate identity. Approval permits source-platform reference playback only; it does not grant reuse rights.',
    loading: 'Loading the review queue…',
    failed: 'The review queue is unavailable right now.',
    retry: 'Try again',
    empty: 'No videos are waiting for Owner review.',
    refresh: 'Refresh queue',
    item: 'Queue item',
    occurrences: 'Source occurrences',
    anatomy: 'Anatomy evidence',
    none: 'Not available',
    qualification: 'Current qualification',
    gates: 'Qualification gates',
    rights: 'Reference rights',
    safety: 'Safety',
    identity: 'Identity',
    sameAs: 'Matching edition ID',
    evidence: 'Evidence URL',
    evidenceHelp: 'Use the source or review URL you actually checked.',
    note: 'Review note',
    submit: 'Record review',
    submitting: 'Recording review…',
    submitted: 'Review recorded. The queue and qualification were refreshed.',
    evidenceRequired: 'Add an evidence URL for every completed decision.',
    sameAsRequired: 'Name the surviving edition when marking this as the same video.',
    submitFailed: 'The review was not recorded. Your selections remain here.',
  },
  ar: {
    eyebrow: 'المالك · بيئة الاختبار فقط',
    title: 'مراجعة مكتبة الفيديو',
    intro:
      'راجع حقوق العرض المرجعي والسلامة وهوية النسخ المكررة. الموافقة تسمح بالعرض من المنصة المصدر فقط ولا تمنح حقوق إعادة الاستخدام.',
    loading: 'جارٍ تحميل قائمة المراجعة…',
    failed: 'قائمة المراجعة غير متاحة الآن.',
    retry: 'إعادة المحاولة',
    empty: 'لا توجد فيديوهات بانتظار مراجعة المالك.',
    refresh: 'تحديث القائمة',
    item: 'عنصر المراجعة',
    occurrences: 'النسخ المنشورة',
    anatomy: 'دليل تحليل الفيديو',
    none: 'غير متاح',
    qualification: 'حالة التأهيل الحالية',
    gates: 'بوابات التأهيل',
    rights: 'حقوق العرض المرجعي',
    safety: 'السلامة',
    identity: 'هوية الفيديو',
    sameAs: 'معرّف النسخة المطابقة',
    evidence: 'رابط الدليل',
    evidenceHelp: 'استخدم رابط المصدر أو المراجعة الذي فحصته فعلاً.',
    note: 'ملاحظة المراجعة',
    submit: 'تسجيل المراجعة',
    submitting: 'جارٍ تسجيل المراجعة…',
    submitted: 'تم تسجيل المراجعة وتحديث القائمة وحالة التأهيل.',
    evidenceRequired: 'أضف رابط دليل لكل قرار مكتمل.',
    sameAsRequired: 'حدد معرّف النسخة الباقية عند اعتبار الفيديو مطابقاً.',
    submitFailed: 'لم تُسجّل المراجعة. بقيت اختياراتك كما هي.',
  },
} as const;

function evidence(reference: string): readonly { kind: 'URL'; reference: string }[] {
  const trimmed = reference.trim();
  return trimmed === '' ? [] : [{ kind: 'URL', reference: trimmed }];
}

function ReviewSelect({
  label,
  value,
  onChange,
  children,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <label className="mj-pool-review-field">
      <span>{label}</span>
      <select
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        {children}
      </select>
    </label>
  );
}

export function VideoPoolReviewPage(): ReactNode {
  const { language } = useLanguage();
  const copy = COPY[language];
  const [entries, setEntries] = useState<readonly PoolReviewQueueEntry[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rights, setRights] = useState<PoolRightsReviewState>('PENDING');
  const [safety, setSafety] = useState<PoolSafetyReviewState>('PENDING');
  const [identity, setIdentity] = useState<PoolIdentityReviewState>('UNCERTAIN_REVIEW');
  const [sameAs, setSameAs] = useState('');
  const [rightsEvidence, setRightsEvidence] = useState('');
  const [safetyEvidence, setSafetyEvidence] = useState('');
  const [identityEvidence, setIdentityEvidence] = useState('');
  const [rightsNote, setRightsNote] = useState('');
  const [safetyNote, setSafetyNote] = useState('');
  const [identityNote, setIdentityNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const result = await fetchPoolReviewQueue();
      setEntries(result.entries);
      setSelectedId((current) =>
        current !== null &&
        result.entries.some((entry) => entry.poolEntryId === current)
          ? current
          : (result.entries[0]?.poolEntryId ?? null),
      );
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => void load(), [load]);

  const selected = useMemo(
    () => entries.find((entry) => entry.poolEntryId === selectedId) ?? null,
    [entries, selectedId],
  );

  const resetForm = useCallback((entry: PoolReviewQueueEntry) => {
    setRights(entry.latestReview?.rightsReference.state ?? 'PENDING');
    setSafety(entry.latestReview?.safety.state ?? 'PENDING');
    setIdentity(entry.latestReview?.identity.state ?? 'UNCERTAIN_REVIEW');
    setSameAs('');
    setRightsEvidence(entry.occurrences[0]?.canonicalUrl ?? '');
    setSafetyEvidence(entry.occurrences[0]?.canonicalUrl ?? '');
    setIdentityEvidence(entry.occurrences[0]?.canonicalUrl ?? '');
    setRightsNote('');
    setSafetyNote('');
    setIdentityNote('');
    setError(null);
    setNotice(null);
  }, []);

  useEffect(() => {
    if (selected !== null) resetForm(selected);
  }, [resetForm, selected]);

  const onSubmit = useCallback(async () => {
    if (selected === null) return;
    const completedWithoutEvidence =
      (rights !== 'PENDING' && rightsEvidence.trim() === '') ||
      (safety !== 'PENDING' && safetyEvidence.trim() === '') ||
      (identity !== 'UNCERTAIN_REVIEW' && identityEvidence.trim() === '');
    if (completedWithoutEvidence) {
      setError(copy.evidenceRequired);
      return;
    }
    if (identity === 'CONFIRMED_SAME_AS' && sameAs.trim() === '') {
      setError(copy.sameAsRequired);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await submitPoolReview({
        reviewId: crypto.randomUUID(),
        poolEntryId: selected.poolEntryId,
        editionId: selected.editionId,
        rightsReference: {
          state: rights,
          evidence: evidence(rightsEvidence),
          note: rightsNote.trim(),
        },
        safety: {
          state: safety,
          evidence: evidence(safetyEvidence),
          note: safetyNote.trim(),
        },
        identity: {
          state: identity,
          sameAsEditionId: identity === 'CONFIRMED_SAME_AS' ? sameAs.trim() : null,
          evidence: evidence(identityEvidence),
          note: identityNote.trim(),
        },
      });
      setNotice(copy.submitted);
      await load();
    } catch {
      setError(copy.submitFailed);
    } finally {
      setBusy(false);
    }
  }, [
    copy,
    identity,
    identityEvidence,
    identityNote,
    load,
    rights,
    rightsEvidence,
    rightsNote,
    safety,
    safetyEvidence,
    safetyNote,
    sameAs,
    selected,
  ]);

  return (
    <div className="mj-pool-review-page">
      <header className="mj-pool-review-heading">
        <div>
          <p>{copy.eyebrow}</p>
          <h1>{copy.title}</h1>
          <p>{copy.intro}</p>
        </div>
        <button
          type="button"
          className="mj-pool-review-secondary"
          onClick={() => void load()}
          disabled={loading}
        >
          <RefreshCw size={16} aria-hidden="true" /> {copy.refresh}
        </button>
      </header>

      {loading ? (
        <Spinner label={copy.loading} />
      ) : failed ? (
        <InlineAlert tone="warning" title={copy.failed}>
          <button
            type="button"
            className="mj-pool-review-secondary"
            onClick={() => void load()}
          >
            {copy.retry}
          </button>
        </InlineAlert>
      ) : entries.length === 0 ? (
        <div className="mj-pool-review-empty">
          <CheckCircle2 size={28} aria-hidden="true" />
          <p>{copy.empty}</p>
        </div>
      ) : (
        <div className="mj-pool-review-layout">
          <nav className="mj-pool-review-queue" aria-label={copy.title}>
            {entries.map((entry, index) => (
              <button
                key={entry.poolEntryId}
                type="button"
                aria-current={entry.poolEntryId === selectedId ? 'true' : undefined}
                onClick={() => {
                  setSelectedId(entry.poolEntryId);
                }}
              >
                <span>
                  {copy.item} {String(index + 1)}
                </span>
                <strong>{entry.editionId}</strong>
                <small>
                  {entry.latestQualification?.status ?? 'PENDING'} ·{' '}
                  {String(entry.occurrences.length)} {copy.occurrences}
                </small>
              </button>
            ))}
          </nav>

          {selected === null ? null : (
            <section
              className="mj-pool-review-card"
              aria-labelledby="mj-pool-review-item-title"
            >
              <div className="mj-pool-review-card-title">
                <span className="mj-pool-review-icon">
                  <ShieldCheck size={22} aria-hidden="true" />
                </span>
                <div>
                  <p>{selected.poolEntryId}</p>
                  <h2 id="mj-pool-review-item-title">{selected.editionId}</h2>
                </div>
              </div>

              <dl className="mj-pool-review-facts">
                <div>
                  <dt>{copy.qualification}</dt>
                  <dd>{selected.latestQualification?.status ?? 'PENDING'}</dd>
                </div>
                <div>
                  <dt>{copy.anatomy}</dt>
                  <dd>{selected.anatomyReference ?? copy.none}</dd>
                </div>
                <div>
                  <dt>{copy.identity}</dt>
                  <dd>{selected.identityState ?? copy.none}</dd>
                </div>
              </dl>

              {selected.latestQualification !== null ? (
                <div className="mj-pool-review-gates">
                  <h3>{copy.gates}</h3>
                  <ul>
                    {Object.entries(selected.latestQualification.gates).map(
                      ([gate, result]) => (
                        <li key={gate}>
                          <span>{gate.replaceAll('_', ' ')}</span>
                          <strong data-state={result.state}>{result.state}</strong>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              ) : null}

              <div className="mj-pool-review-occurrences">
                <h3>{copy.occurrences}</h3>
                {selected.occurrences.map((occurrence) => (
                  <a
                    key={occurrence.occurrenceId}
                    href={occurrence.canonicalUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>
                      {occurrence.publisherDisplayName ??
                        occurrence.publisherHandle ??
                        occurrence.platform}
                    </span>
                    <small className="mj-ltr">{occurrence.canonicalUrl}</small>
                    <ExternalLink size={15} aria-hidden="true" />
                  </a>
                ))}
              </div>

              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void onSubmit();
                }}
              >
                <fieldset>
                  <legend>{copy.rights}</legend>
                  <ReviewSelect
                    label={copy.rights}
                    value={rights}
                    onChange={(value) => {
                      setRights(value as PoolRightsReviewState);
                    }}
                  >
                    <option value="PENDING">PENDING</option>
                    <option value="REFERENCE_APPROVED">REFERENCE APPROVED</option>
                    <option value="REJECTED">REJECTED</option>
                  </ReviewSelect>
                  <label className="mj-pool-review-field">
                    <span>{copy.evidence}</span>
                    <input
                      className="mj-ltr"
                      type="url"
                      value={rightsEvidence}
                      onChange={(event) => {
                        setRightsEvidence(event.target.value);
                      }}
                    />
                    <small>{copy.evidenceHelp}</small>
                  </label>
                  <label className="mj-pool-review-field">
                    <span>{copy.note}</span>
                    <textarea
                      maxLength={2000}
                      value={rightsNote}
                      onChange={(event) => {
                        setRightsNote(event.target.value);
                      }}
                    />
                  </label>
                </fieldset>
                <fieldset>
                  <legend>{copy.safety}</legend>
                  <ReviewSelect
                    label={copy.safety}
                    value={safety}
                    onChange={(value) => {
                      setSafety(value as PoolSafetyReviewState);
                    }}
                  >
                    <option value="PENDING">PENDING</option>
                    <option value="APPROVED">APPROVED</option>
                    <option value="REJECTED">REJECTED</option>
                  </ReviewSelect>
                  <label className="mj-pool-review-field">
                    <span>{copy.evidence}</span>
                    <input
                      className="mj-ltr"
                      type="url"
                      value={safetyEvidence}
                      onChange={(event) => {
                        setSafetyEvidence(event.target.value);
                      }}
                    />
                    <small>{copy.evidenceHelp}</small>
                  </label>
                  <label className="mj-pool-review-field">
                    <span>{copy.note}</span>
                    <textarea
                      maxLength={2000}
                      value={safetyNote}
                      onChange={(event) => {
                        setSafetyNote(event.target.value);
                      }}
                    />
                  </label>
                </fieldset>
                <fieldset>
                  <legend>{copy.identity}</legend>
                  <ReviewSelect
                    label={copy.identity}
                    value={identity}
                    onChange={(value) => {
                      setIdentity(value as PoolIdentityReviewState);
                    }}
                  >
                    <option value="UNCERTAIN_REVIEW">UNCERTAIN REVIEW</option>
                    <option value="CONFIRMED_DISTINCT">CONFIRMED DISTINCT</option>
                    <option value="CONFIRMED_SAME_AS">CONFIRMED SAME AS</option>
                  </ReviewSelect>
                  {identity === 'CONFIRMED_SAME_AS' ? (
                    <label className="mj-pool-review-field">
                      <span>{copy.sameAs}</span>
                      <input
                        className="mj-ltr"
                        value={sameAs}
                        onChange={(event) => {
                          setSameAs(event.target.value);
                        }}
                      />
                    </label>
                  ) : null}
                  <label className="mj-pool-review-field">
                    <span>{copy.evidence}</span>
                    <input
                      className="mj-ltr"
                      type="url"
                      value={identityEvidence}
                      onChange={(event) => {
                        setIdentityEvidence(event.target.value);
                      }}
                    />
                    <small>{copy.evidenceHelp}</small>
                  </label>
                  <label className="mj-pool-review-field">
                    <span>{copy.note}</span>
                    <textarea
                      maxLength={2000}
                      value={identityNote}
                      onChange={(event) => {
                        setIdentityNote(event.target.value);
                      }}
                    />
                  </label>
                </fieldset>
                {error !== null ? (
                  <InlineAlert tone="error" role="alert">
                    {error}
                  </InlineAlert>
                ) : null}
                {notice !== null ? (
                  <InlineAlert tone="success" role="status">
                    {notice}
                  </InlineAlert>
                ) : null}
                <button
                  type="submit"
                  className="mj-pool-review-primary"
                  disabled={busy}
                >
                  {busy ? copy.submitting : copy.submit}
                </button>
              </form>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
